import { useEffect, useState, useRef, useCallback } from 'react';
import { Trash2, Upload, FileText, Mail, Sparkles } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import {
  getExpenseAttachments, uploadExpenseAttachment, deleteExpenseAttachment,
  type ExpenseAttachment,
} from '../api';
import Lightbox from './Lightbox';

interface Props {
  expenseId: number;
  viewOnly?: boolean;
}

// Bilag-sektion i Expense-modal. Vises kun nar expense har et id.
// Drag-drop + klik-upload. Viser thumbnail for billeder, PDF-ikon for PDFs.
export default function ExpenseAttachmentsSection({ expenseId, viewOnly = false }: Props) {
  const { showError, showSuccess } = useToast();
  const [items, setItems] = useState<ExpenseAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    try { setItems(await getExpenseAttachments(expenseId)); }
    catch (e) { showError(e instanceof Error ? e.message : 'Kunne ikke hente bilag'); }
    finally { setLoading(false); }
  }, [expenseId, showError]);

  useEffect(() => { reload(); }, [reload]);

  const upload = async (files: FileList | File[]) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        await uploadExpenseAttachment(expenseId, f);
      }
      await reload();
      showSuccess('Bilag tilfojet');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Upload fejlede');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (att: ExpenseAttachment) => {
    if (!confirm(`Slet ${att.filename}?`)) return;
    try {
      await deleteExpenseAttachment(expenseId, att.id);
      await reload();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Kunne ikke slette');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (viewOnly) return;
    const files = e.dataTransfer.files;
    if (files.length > 0) upload(files);
  };

  const sourceIcon = (s: ExpenseAttachment['source']) => {
    if (s === 'email') return <Mail size={11} />;
    if (s === 'ai_scan') return <Sparkles size={11} />;
    return null;
  };
  const sourceLabel = (s: ExpenseAttachment['source']) => {
    if (s === 'email') return 'Mail';
    if (s === 'ai_scan') return 'AI-scan';
    return null;
  };

  if (loading) return <p className="text-sm text-gray-400">...</p>;

  return (
    <div className="space-y-3">
      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map(att => {
            const isImage = att.mime_type?.startsWith('image/');
            return (
              <div key={att.id} className="relative group border border-ink-100 rounded-lg overflow-hidden bg-cream-50">
                <div className="aspect-[4/3] cursor-pointer"
                  onClick={() => isImage ? setLightboxSrc(att.image_url) : window.open(att.image_url, '_blank', 'noopener,noreferrer')}>
                  {isImage ? (
                    <img src={att.image_url} alt={att.filename} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-ink-300 gap-1">
                      <FileText size={28} />
                      <span className="text-[10px] uppercase tracking-wide">{att.mime_type?.includes('pdf') ? 'PDF' : 'FIL'}</span>
                    </div>
                  )}
                </div>
                <div className="px-2 py-1.5 text-[11px] text-ink-600 flex items-center justify-between gap-1 border-t border-ink-100">
                  <span className="truncate flex-1" title={att.filename}>{att.filename}</span>
                  {sourceLabel(att.source) && (
                    <span className="inline-flex items-center gap-0.5 text-ink-400 flex-shrink-0" title={sourceLabel(att.source) ?? ''}>
                      {sourceIcon(att.source)}
                    </span>
                  )}
                </div>
                {!viewOnly && (
                  <button type="button" onClick={() => handleDelete(att)}
                    className="absolute top-1.5 right-1.5 bg-black/50 hover:bg-rose-600 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Slet bilag">
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!viewOnly && (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            dragOver ? 'border-[var(--color-primary)] bg-cream-100' : 'border-gray-300'
          }`}>
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
            onChange={e => { const f = e.target.files; if (f && f.length > 0) upload(f); }} />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
            <Upload size={14} />
            {uploading ? 'Uploader...' : items.length > 0 ? 'Tilfoej flere' : 'Vaelg fil(er) eller traek herhen'}
          </button>
          <p className="text-xs text-gray-400 mt-1">Billeder + PDF, maks 10 MB pr fil</p>
        </div>
      )}

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
